import { matchFigures } from '@versesverse/core';
import type { Searchable } from '@versesverse/core';

/*
 * WHAT A RESULT ROW SAYS, AND THE ONE THING OURS DOES NOT — compared 2026-08-22.
 *
 * Theirs, for "Jo": `Joseph / Yusuf · Person · 3 corpora · beyond reach`. Ours matches all of it
 * except the last part — the paired other name, the kind, and the corpora written out when there are
 * few (`Person · Hebrew Bible & New Testament`) or counted when there are many.
 *
 * "beyond reach" is the frontier, and it is the same decision standing open with Ariel that
 * `panel/trails.ts` records at length: theirs refuses to open what the reader has not walked to and
 * ours opens everything, so a row that says "beyond reach" and then opens would be stating a limit
 * this app does not keep. When that decision lands it lands here too, not only on the trail chips.
 *
 * (The ORDER differs and is not this: theirs leads with Joseph and ours with Joshua, because theirs
 * ranks on an importance number 1-10 that we do not carry and ours on how many scriptures name a
 * figure. That is the data difference the port was given from the start.)
 *
 * THE ACCESSIBLE WAY IN. The map is a canvas — unreachable by keyboard or screen reader — so
 * search is not a convenience here, it is the path. core/search.ts does the matching (by display
 * name AND by any spelling the texts use, so "Musa" reaches Moses); this ranks and caps the
 * results so the most obvious match is first and the list stays a list, not a scroll.
 *
 * AND WITHIN A TIER IT RANKS BY HOW MUCH IS SAID ABOUT A FIGURE. The tie-break used to be the
 * SHORTER NAME, which is a rule about spelling rather than about what a reader wants: typing "Jo"
 * returned Job, Joab, Joah, Joash, Jobab — five names of three to five letters — while Joseph and
 * Joshua, at six, waited below them. Theirs is engine.js:2031,
 *
 *     allHits.sort((a,b) => (known(b)?1:0) - (known(a)?1:0) || b.w - a.w)
 *
 * — what you have already walked first, then the weightiest. Ours keeps its four match tiers, which
 * theirs has no equivalent of and which are better (an exact name should beat a contains), and
 * replaces the length rule with the citation count. The KNOWN half of theirs is not ported: this
 * function is not told what the reader has walked, and wiring the fog into the search is a bigger
 * change than the one this note is about.
 */
export function searchNodes<T extends Searchable>(nodes: T[], term: string, limit = 20): T[] {
  const t = term.trim().toLowerCase();
  if (!t) return [];

  /** 0 exact, 1 starts-with, 2 contains, 3 matched only by a spelling form. */
  const rank = (n: T): number => {
    const name = n.name.toLowerCase();
    if (name === t) return 0;
    if (name.startsWith(t)) return 1;
    if (name.includes(t)) return 2;
    return 3;
  };

  return [...matchFigures(nodes, term)]
    .sort((a, b) => rank(a) - rank(b)
      /* The weightiest first, as theirs does — a name the library says more about is the one a
         half-typed query is most likely reaching for. */
      || (b.total ?? 0) - (a.total ?? 0)
      /* Then the one held by more texts, which is the map's own measure of a shared figure. */
      || b.corpora.length - a.corpora.length
      /* And only then the older rules, so a roster carrying no counts ranks exactly as before. */
      || a.name.length - b.name.length
      || a.name.localeCompare(b.name))
    .slice(0, limit);
}
